import api from './api';
import type {
  PagedResult,
  Library,
  LibraryDetail,
  LibraryCourse,
  LibraryQuery,
  LibraryCourseQuery,
  CreateLibrary,
  UpdateLibrary,
  AddLibraryCourse,
  BulkAddLibraryCourses,
  BulkAddResult,
} from '@/types/catalog';
import { buildQueryString } from '@/utils/buildQueryString';

/**
 * A row from the library list endpoint: the shared `Library` shape plus the
 * admin-only columns that endpoint adds. Named rather than inlined so
 * `getAll` and `getAllPages` cannot drift apart, and so neither has to infer
 * its return type from the other.
 */
export type LibraryListItem = Library & {
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string | null;
  learningObjectCount: number;
};

export const libraryApi = {
  /**
   * Gets a paginated list of libraries with optional filtering.
   */
  getAll: async (query: LibraryQuery = {}): Promise<PagedResult<LibraryListItem>> => {
    const qs = buildQueryString(query as Record<string, unknown>);
    const response = await api.get<PagedResult<LibraryListItem>>(`/libraries${qs}`);
    return response.data;
  },

  /**
   * Gets **every** library matching the query, following pagination to the end.
   *
   * For pickers, which need the whole set rather than a page of it. A fixed
   * `limit` silently truncates: the list endpoint defaults to sorting by
   * DisplayOrder then Name, and newly created libraries typically carry
   * DisplayOrder 0, so they sort alphabetically among every other
   * DisplayOrder-0 library rather than landing at the end. A library can
   * therefore fall outside a fixed limit purely because of its name, and the
   * picker shows no error — the library is simply missing.
   *
   * Pages stay at 100. Do not "simplify" this to one big `limit` — the list
   * query includes each library's VariantMemberships, and an oversized page
   * has OOM'd this endpoint before (see LibraryRepository.GetPagedAsync).
   */
  getAllPages: async (
    query: Omit<LibraryQuery, 'page' | 'limit'> = {},
  ): Promise<LibraryListItem[]> => {
    const pageSize = 100;
    const first = await libraryApi.getAll({ ...query, limit: pageSize });
    const items = [...first.items];

    const totalPages = Math.ceil(first.totalCount / pageSize);
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          libraryApi.getAll({ ...query, limit: pageSize, page: i + 2 })),
      );
      for (const page of rest) {
        items.push(...page.items);
      }
    }

    return items;
  },

  /**
   * Gets a single library by ID with full details including courses.
   */
  getById: async (id: number): Promise<LibraryDetail> => {
    const response = await api.get<LibraryDetail>(`/libraries/${id}`);
    return response.data;
  },

  /**
   * Gets a single library by slug with full details including courses.
   */
  getBySlug: async (slug: string): Promise<LibraryDetail> => {
    const response = await api.get<LibraryDetail>(`/libraries/slug/${slug}`);
    return response.data;
  },

  /**
   * BPE-732: one page of a library's courses, with search and recorded-date
   * filtering applied server-side. Replaces the course collection the detail
   * payload used to embed — every course in the library, unpaged, 4.4 MB for
   * CLE — which the client then searched, filtered and paged in memory.
   */
  getCourses: async (
    libraryId: number,
    query: LibraryCourseQuery = {},
  ): Promise<PagedResult<LibraryCourse>> => {
    const qs = buildQueryString(query as Record<string, unknown>);
    const response = await api.get<PagedResult<LibraryCourse>>(
      `/libraries/${libraryId}/learning-objects${qs}`,
    );
    return response.data;
  },

  /**
   * BPE-732: every course id in the library. The add-courses picker greys out
   * what the library already holds, which a page of rows can't answer. Ids are
   * cheap where rows are not, so this one stays unpaged.
   */
  getCourseIds: async (libraryId: number): Promise<number[]> => {
    const response = await api.get<number[]>(`/libraries/${libraryId}/learning-object-ids`);
    return response.data;
  },

  /**
   * Gets the libraries the current learner can access (active + published),
   * ordered for display. Drives the Content Library navigation.
   */
  getMine: async (): Promise<Library[]> => {
    const response = await api.get<{ success: boolean; data: { libraries: Library[] } }>('/me/libraries');
    return response.data.data.libraries;
  },

  /**
   * Creates a new library.
   */
  create: async (library: CreateLibrary): Promise<Library> => {
    const response = await api.post<Library>('/libraries', library);
    return response.data;
  },

  /**
   * Updates an existing library.
   */
  update: async (id: number, library: UpdateLibrary): Promise<Library> => {
    const response = await api.put<Library>(`/libraries/${id}`, library);
    return response.data;
  },

  /**
   * Clones a library into a new, independent draft library. Copies the source's
   * course references (not its contract/pass-type access mappings). The optional
   * name defaults server-side to "<source> (Copy)" when omitted.
   */
  clone: async (id: number, name?: string): Promise<Library> => {
    const response = await api.post<Library>(`/libraries/${id}/clone`, { name });
    return response.data;
  },

  /**
   * Deletes a library.
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/libraries/${id}`);
  },

  /**
   * Adds a course to a library.
   */
  addCourse: async (libraryId: number, dto: AddLibraryCourse): Promise<LibraryCourse> => {
    const response = await api.post<LibraryCourse>(`/libraries/${libraryId}/learning-objects`, dto);
    return response.data;
  },

  /**
   * Bulk-adds courses to a library in one request (the picker's "select all" → Add
   * Selected flow). Idempotent and best-effort: the result reports per-item success,
   * including already-present, conflicting, or missing courses, without failing the batch.
   */
  addCoursesBulk: async (libraryId: number, dto: BulkAddLibraryCourses): Promise<BulkAddResult> => {
    const response = await api.post<BulkAddResult>(`/libraries/${libraryId}/learning-objects/bulk`, dto);
    return response.data;
  },

  /**
   * Removes a course from a library.
   */
  removeCourse: async (libraryId: number, learningObjectId: number): Promise<void> => {
    await api.delete(`/libraries/${libraryId}/learning-objects/${learningObjectId}`);
  },

  /**
   * Publishes a library (shortcut for updating status to published).
   */
  publish: async (id: number): Promise<Library> => {
    return libraryApi.update(id, { status: 'published' });
  },

  /**
   * Unpublishes a library (shortcut for updating status to draft).
   */
  unpublish: async (id: number): Promise<Library> => {
    return libraryApi.update(id, { status: 'draft' });
  },
};

export default libraryApi;
